import { useState, useEffect, useRef, ReactNode } from 'react';
import { X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface MobileBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

type SheetPosition = 'hidden' | 'partial' | 'full';

export function MobileBottomSheet({ isOpen, onClose, title, children }: MobileBottomSheetProps) {
  const { t } = useTranslation();
  const [position, setPosition] = useState<SheetPosition>('hidden');
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Update position when isOpen changes
  useEffect(() => {
    if (isOpen) {
      setPosition('partial');
      setDragOffset(0);
    } else {
      setPosition('hidden');
      setDragOffset(0);
    }
  }, [isOpen]);

  // Get base translate for position
  // partial = 90% zakrytí obrazovky (translateY = 10% windowHeight)
  const getBaseTranslate = (pos: SheetPosition, windowHeight: number): number => {
    if (pos === 'hidden') return windowHeight;
    if (pos === 'partial') return windowHeight * 0.1; // 90% zakrytí
    return 0; // full (nepoužívá se, ale ponecháno pro kompatibilitu)
  };

  // Calculate position based on current translate
  const getPositionFromTranslate = (translate: number, windowHeight: number): SheetPosition => {
    // Threshold mezi partial (10%) a hidden (100%) = 55%
    const closeThreshold = windowHeight * 0.55;

    if (translate > closeThreshold) {
      return 'hidden';
    } else {
      return 'partial'; // Vždy vracíme partial, protože chceme vždy 90% zakrytí
    }
  };

  // Handle touch start - pouze na drag handle nebo horní části
  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    // Ignorovat touch eventy na interaktivních prvcích (tlačítka, accordion, atd.)
    if (target.closest('button') || 
        target.closest('[role="button"]') || 
        target.closest('[data-radix-accordion-trigger]') ||
        target.closest('[data-radix-select-trigger]') ||
        target.closest('[data-radix-popover-trigger]')) {
      return;
    }
    // Povolit dragování pouze na drag handle nebo v header oblasti
    if (target.closest('[data-drag-handle]') || target.closest('[data-sheet-header]')) {
      setIsDragging(true);
      setStartY(e.touches[0].clientY);
      setDragOffset(0);
      setIsScrolling(false);
    }
  };

  // Handle touch move
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const touchY = e.touches[0].clientY;
    const deltaY = touchY - startY;
    const windowHeight = window.innerHeight;
    const baseTranslate = getBaseTranslate(position, windowHeight);
    
    // Pokud táhneme dolů, je to dragování bottom sheetu
    // Pokud táhneme nahoru, může to být scrollování
    if (deltaY > 0) {
      // Táhneme dolů - určitě dragování
      setIsScrolling(false);
      const newTranslate = baseTranslate + deltaY;
      const clampedTranslate = Math.max(0, Math.min(windowHeight, newTranslate));
      const newOffset = clampedTranslate - baseTranslate;
      setDragOffset(newOffset);
      e.preventDefault();
    } else if (deltaY < -10) {
      // Táhneme nahoru o více než 10px - pravděpodobně scrollování
      setIsScrolling(true);
      setIsDragging(false);
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (!isDragging || isScrolling) {
      setIsDragging(false);
      setIsScrolling(false);
      return;
    }

    setIsDragging(false);
    setIsScrolling(false);
    const windowHeight = window.innerHeight;
    const baseTranslate = getBaseTranslate(position, windowHeight);
    const currentTranslate = baseTranslate + dragOffset;
    
    const newPosition = getPositionFromTranslate(currentTranslate, windowHeight);
    setPosition(newPosition);
    setDragOffset(0);

    // Close if hidden
    if (newPosition === 'hidden') {
      onClose();
    }
  };

  // Get transform value based on position and current drag
  const getTransform = (): string => {
    const windowHeight = window.innerHeight;
    const baseTranslate = getBaseTranslate(position, windowHeight);
    const finalTranslate = baseTranslate + dragOffset;

    return `translateY(${finalTranslate}px)`;
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Don't render if completely hidden and not open
  if (!isOpen && position === 'hidden' && dragOffset === 0) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={handleBackdropClick}
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50 bg-white-cream backdrop-blur-lg',
          'rounded-t-3xl border-t border-wood-warm/30 shadow-2xl',
          'transition-transform duration-300 ease-out flex flex-col',
          isDragging && 'transition-none'
        )}
        style={{
          transform: getTransform(),
          height: '90vh',
        }}
      >
        {/* Drag Handle */}
        <div
          ref={dragHandleRef}
          data-drag-handle
          className="flex items-center justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing flex-shrink-0"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1.5 bg-charcoal-medium/30 rounded-full" />
        </div>

        {/* Header */}
        <div 
          data-sheet-header
          className="flex items-center justify-between px-6 pb-4 border-b border-wood-warm/20 flex-shrink-0"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {title && (
            <h2 className="text-xl font-bold text-foreground">{title}</h2>
          )}
          <button
            onClick={onClose}
            className="ml-auto p-2 -mr-2 rounded-lg hover:bg-beige-warm transition-colors"
            aria-label="Zavřít"
          >
            <X className="w-5 h-5 text-charcoal-medium" />
          </button>
        </div>

        {/* Content - scrollovatelné */}
        <div 
          ref={contentRef} 
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="p-6 pb-24">
            {children}
          </div>
        </div>

        {/* Aplikovat tlačítko v kontejneru o šířce displeje */}
        <div className="absolute bottom-0 left-0 right-0 z-10 flex-shrink-0 bg-white-cream/95 backdrop-blur-lg border-t border-wood-warm/30 shadow-lg">
          <div className="p-4">
            <button
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-green-dark hover:bg-green-forest text-white-pure rounded-2xl shadow-md font-semibold transition-all duration-200 active:scale-95"
              aria-label={t('common.apply', { defaultValue: 'Aplikovat' })}
            >
              <Check className="w-5 h-5" />
              <span>{t('common.apply', { defaultValue: 'Aplikovat' })}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}


